import React, { useState, useContext, useEffect } from "react";
import { CurrentUserContext } from "../../App";
import sampleAvatar from "../../assets/sampleAvatar.png";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3080";

function formatTimeAgo(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export default function ChatListComponent({
  conversations,
  selectedConversationId,
  onSelectConversation,
  onNewChat,
  currentUserId,
}) {
  const [searchQuery, setSearchQuery] = useState("");

  const filteredConversations = conversations.filter((convo) => {
    if (!searchQuery.trim()) return true;
    const name = convo.is_group
      ? convo.group_name || "Group"
      : convo.other_fullname || convo.other_username || "User";
    return name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  return (
    <div
      className="d-flex flex-column h-100"
      style={{
        backgroundColor: "#1a1a2e",
        borderRight: "1px solid #2a2a3e",
        width: "100%",
      }}
    >
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between p-3">
        <h5 className="text-white mb-0 fw-bold" style={{ fontSize: "1.8rem" }}>
          Chats
        </h5>
        <button
          className="btn btn-sm rounded-circle d-flex align-items-center justify-content-center"
          onClick={onNewChat}
          style={{
            backgroundColor: "var(--chat-purple)",
            color: "white",
            width: "36px",
            height: "36px",
            border: "none",
          }}
          title="New Chat"
        >
          <ion-icon name="create-outline" style={{ fontSize: "1.6rem" }}></ion-icon>
        </button>
      </div>

      {/* Search */}
      <div className="px-3 pb-2">
        <div
          className="d-flex align-items-center rounded-pill px-3"
          style={{ backgroundColor: "#2a2a3e", height: "40px" }}
        >
          <ion-icon
            name="search-outline"
            style={{ color: "#828282", fontSize: "1.4rem" }}
          ></ion-icon>
          <input
            type="text"
            placeholder="Search chats..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="border-0 bg-transparent text-white ms-2 w-100"
            style={{ outline: "none", fontSize: "1.4rem" }}
          />
        </div>
      </div>

      {/* Conversation List */}
      <div className="flex-grow-1" style={{ overflowY: "auto" }}>
        {filteredConversations.length === 0 ? (
          <div className="text-center py-5" style={{ color: "#828282" }}>
            <ion-icon
              name="chatbubbles-outline"
              style={{ fontSize: "4rem" }}
            ></ion-icon>
            <p className="mt-2" style={{ fontSize: "1.4rem" }}>
              {conversations.length === 0
                ? "No conversations yet"
                : "No matching chats"}
            </p>
          </div>
        ) : (
          filteredConversations.map((convo) => {
            const isSelected =
              selectedConversationId === convo.conversation_id;
            const displayName = convo.is_group
              ? convo.group_name || "Group"
              : convo.other_first_name
              ? `${convo.other_first_name} ${convo.other_last_name || ""}`
              : convo.other_username || "User";
            const rawAvatar = convo.is_group
              ? convo.group_avatar
              : convo.other_avatar;
            const avatarUrl = rawAvatar && rawAvatar !== "sampleAvatar.png" ? rawAvatar : null;
            let lastMessagePreview = "";
            if (convo.last_message_content) {
              if (convo.last_message_is_deleted) {
                lastMessagePreview = "Message deleted";
              } else {
                lastMessagePreview =
                  convo.last_message_content.length > 40
                    ? convo.last_message_content.substring(0, 40) + "..."
                    : convo.last_message_content;
              }
            } else if (convo.last_message_id) {
              lastMessagePreview = "Sent an image";
            }

            return (
              <div
                key={convo.conversation_id}
                className="d-flex align-items-center px-3 py-2 gap-3"
                onClick={() => onSelectConversation(convo)}
                style={{
                  cursor: "pointer",
                  backgroundColor: isSelected
                    ? "rgba(93, 82, 255, 0.15)"
                    : "transparent",
                  borderLeft: isSelected
                    ? "3px solid var(--chat-purple)"
                    : "3px solid transparent",
                  transition: "background-color 0.15s",
                }}
                onMouseEnter={(e) => {
                  if (!isSelected)
                    e.currentTarget.style.backgroundColor =
                      "rgba(255,255,255,0.05)";
                }}
                onMouseLeave={(e) => {
                  if (!isSelected)
                    e.currentTarget.style.backgroundColor = "transparent";
                }}
              >
                {/* Avatar */}
                <div style={{ minWidth: "48px", width: "48px", height: "48px" }}>
                  <img
                    src={avatarUrl || sampleAvatar}
                    alt={displayName}
                    className="rounded-circle w-100 h-100"
                    style={{ objectFit: "cover" }}
                  />
                </div>

                {/* Name + preview */}
                <div className="flex-grow-1 overflow-hidden">
                  <div className="d-flex justify-content-between align-items-center">
                    <span
                      className="text-white fw-semibold text-truncate"
                      style={{ fontSize: "1.4rem" }}
                    >
                      {displayName}
                    </span>
                    <span
                      style={{
                        color: "#828282",
                        fontSize: "1.1rem",
                        whiteSpace: "nowrap",
                        marginLeft: "8px",
                      }}
                    >
                      {formatTimeAgo(convo.last_message_time)}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <span
                      className="text-truncate"
                      style={{
                        color: convo.unread_count > 0 ? "#d6d6d6" : "#828282",
                        fontSize: "1.2rem",
                        fontWeight: convo.unread_count > 0 ? "600" : "400",
                      }}
                    >
                      {lastMessagePreview || "No messages yet"}
                    </span>
                    {convo.unread_count > 0 && (
                      <span
                        className="badge rounded-pill d-flex align-items-center justify-content-center"
                        style={{
                          backgroundColor: "var(--chat-purple)",
                          color: "white",
                          fontSize: "1rem",
                          minWidth: "20px",
                          height: "20px",
                          marginLeft: "8px",
                        }}
                      >
                        {convo.unread_count > 99 ? "99+" : convo.unread_count}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
